
/** @jsxImportSource @emotion/react */
import MikeVisualizerLib from '../../MikeVisualizer/lib/MikeVisualizer';
import { EBaseMapIds, getBaseMapConfiguration } from '../../MikeVisualizer/MikeBaseMapConfigurations';
import { MikeVisualizer } from '../../MikeVisualizer';
import { MikeVisualizerContainer, MikeVisualizerStoryDetails } from '../../VisualizerWrapper';
import { IViewerBounds } from '../../MikeVisualizer/lib/IMikeVisualizerModels';
import mikeSharedTheme from '../../styles/mikeSharedTheme';
import { css } from '@emotion/react';
import MikeButton from '../mike-button/MikeButton';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getSortedPageRows } from '../DataTable/support';
import DataTable from '../DataTable/DataTable';
import columns, { CREATEDBY, UPDATEDBY } from '../../model/tablecolumns';
import ViewerLegend from '../ViewerLegend/ViewerLegend';
import { EXISTING_MEASURES_ID, layerGroupTitleAllAreas, layerGroupTitleOwnAreas, OTHER_EXISTING_MEASURES_ID, OWN_MEASURE_AREA_ID, RIVER_ID } from '../../model/layerGroups';
import { makeDefaultVectorStyle, MAP_COLORS } from '../ViewerLegend/utils';
import MeasureDialog from '../MeasureDialog/MeasureDialog';
import MeasureButtonPanel from '../MeasureButtonPanel/MeasureButtonPanel';
import { useAppDispatch } from '../../store/store';
import {  measureDialogOpened, measureSet } from '../../reducers/measures';
import { useTypedSelector } from '../../reducers';
import { IGetMeasure, IRiverResponse, IWorkingAreaResponse, useGetMeasuresQuery, useGetProjectDevelopersQuery, useGetRiverQuery, useGetWorkingAreaQuery } from '../../apis/apiSlice';
import { MEASURE_AREA_ID } from '../../model/layerGroups';
import { actionFields, getActionFieldFromMeasureType, getAreaDisplayName, getEUAspectfromActionField, ISelectableValue } from '../../model/measure';
import { createPoint, createPointFeatureCollection, getPolygonFeatureCollection, getPolylineFeatureCollection } from '../../helpers/map';
import { applyMeasureVisibility, legendGroupNameHideAll, setAreaLayersLoaded } from '../../reducers/legend';
import MIKE_COLORS from '../../styles/mike-colors';
import Edit from '../../icons/Edit.svg?react';
import ZoomToArea from '../../icons/ZoomToArea.svg?react';
import { actionStyle, iconStyle } from '../DataTable/renderers';
import FormControl from '@mui/material/FormControl';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import { Autocomplete, TextField, Typography } from '@mui/material';
import FilterInput from '../FilterInput';
import ILegendLayer from '../../model/ILegendLayer';
import { IPressEvent } from '../../MikeVisualizer/lib/models/IInteractionEvent';
import MeasureInfo from '../MeasureInfo/MeasureInfo';
import ViewerTools from '../ViewerTools';
import { IColumn } from '../../model/IColumn';

function Viewer() {

  const tableModesStyle = css`       
    display: flex;  
    background-color: white;   
    align-items: center;     
 `;

 const buttonStyle = css`
  padding-right: ${mikeSharedTheme.spacing(1)};  
  height: ${mikeSharedTheme.spacing(9)};    
`;

const measureIdFilterContainer = css`
  display: flex;
  align-items: center;  
  border: 2px solid #0B4566 !important; 
  margin-left: ${mikeSharedTheme.spacing(1)}; 
 
  height: ${mikeSharedTheme.spacing(9)};
`;

 const cycleOptionFilterLabel = css` 
  padding-left: ${mikeSharedTheme.spacing(1)};
`;

 const cycleOptionStyle = css`   
  border: 2px solid #0B4566 !important; 
  margin-left: ${mikeSharedTheme.spacing(1)};  
  min-height: ${mikeSharedTheme.spacing(9)}; 
  width: 20%;
`;

 const filterContainerStyle = (width: string) => {
  return css`
    display: flex;
    align-items: center;  
    border: 2px solid #0B4566 !important; 
    margin-left: ${mikeSharedTheme.spacing(1)};    
    width: ${width};
    height: ${mikeSharedTheme.spacing(9)};   
  `;
}

  const { update2DData, enableClickPointPicker, onPressExtended, getState } = MikeVisualizerLib; 
  const { dataMap } = getState();
  const [cycleOption, setCycleOption] = useState("all");
  const [measureIdFilter, setMeasureIdFilter] = useState("");
  const [ownerCompanyNameFilter, setOwnerCompanyNameFilter] = useState<ISelectableValue | undefined>(undefined);
  const [actionFieldFilter, setActionFieldFilter] = useState<ISelectableValue | undefined>(undefined);
  const [createdByFilter, setCreatedByFilter] = useState<ISelectableValue | undefined>(undefined);
  const [updatedByFilter, setUpdatedByFilter] = useState<ISelectableValue | undefined>(undefined);

  const dispatch = useAppDispatch();
  const config = useTypedSelector((state) => state.auth.config);
  const open = useTypedSelector((state) => state.measures.measureDialogOpen);
  const areaLayersLoaded = useTypedSelector((state) => state.legend.areaLayersLoaded);
  const drawing = useTypedSelector((state) => state.legend.drawing);

  const { data: areasFromApi, isSuccess } = useGetWorkingAreaQuery(undefined, {skip: config === null});
  const { data: riversFromApi, isSuccess: isSuccessRiver } = useGetRiverQuery(undefined, {skip: config === null});
  const { data: measuresFromApi, isLoading: isLoadingMeasures, isFetching: isFetchingMeasures/* , isSuccess: isSuccessMeasures, isError: isErrorMeasures */  } = useGetMeasuresQuery(null, {skip: config === null});
  const {data: projectDevelopersFromApi} = useGetProjectDevelopersQuery(undefined, {skip: config === null});;

  const drawRivers = async (items: Array<IRiverResponse>) => {   
    const fc = getPolylineFeatureCollection(items);  
    await update2DData(fc, RIVER_ID, undefined, undefined, undefined, makeDefaultVectorStyle(MIKE_COLORS.BRANDBLUE_DEFAULT, 4));   
  }


  const drawAreaLayer = async (items: Array<IWorkingAreaResponse>, allowedNames: Array<string>, otherAreas: Array<IWorkingAreaResponse>, otherNames: Array<string>) => {        
      let otherAreaLayers = Array<ILegendLayer>();
      let ownAreaLayers = Array<ILegendLayer>();
      otherNames.forEach(async (otherAreaName: string, index: number) => {
          const otherItemsPerName = otherAreas.filter((area: IWorkingAreaResponse) => area.sourceTableName === otherAreaName);
          if (otherItemsPerName.length > 0){
            const fc = getPolygonFeatureCollection(otherItemsPerName); 
            const layer = { groupTitle: layerGroupTitleAllAreas, title: getAreaDisplayName(otherAreaName), id: MEASURE_AREA_ID + index, order: index, visible: true,isTwoD: true,loaded: true};     
            otherAreaLayers = [...otherAreaLayers, layer];           
            await update2DData(fc, MEASURE_AREA_ID + index, undefined, undefined, undefined, makeDefaultVectorStyle(MAP_COLORS.GRAPHITE)); 
          }
      })   
      allowedNames.forEach(async (allowedAreaName: string, index: number) => {
          const itemsPerName = items.filter((area: IWorkingAreaResponse) => area.sourceTableName === allowedAreaName);
          if (itemsPerName.length > 0){
            const fc = getPolygonFeatureCollection(itemsPerName); 
            const layer = { groupTitle: layerGroupTitleOwnAreas, title: getAreaDisplayName(allowedAreaName), id: OWN_MEASURE_AREA_ID + index, order: index, visible: true,isTwoD: true,loaded: true};     
            ownAreaLayers = [...ownAreaLayers, layer];           
            await update2DData(fc, OWN_MEASURE_AREA_ID + index, undefined, undefined, undefined, makeDefaultVectorStyle(MAP_COLORS.BURGUNDYPLUM)); 
          }
      })     
      dispatch(setAreaLayersLoaded([{
        color: MAP_COLORS.BURGUNDYPLUM,
        title: layerGroupTitleOwnAreas,
        hideAllLayers: false,
        layer: ownAreaLayers
      },
      {
        color: MAP_COLORS.GRAPHITE,
        title: layerGroupTitleAllAreas,
        hideAllLayers: false,
        layer: otherAreaLayers
      }   
    ]));        
  }; 

   const onEdit = (row: IGetMeasure) => {
   /*    const disabledForSecondCycle = row.updatedInCycle === 1 || (row.createdInCycle === 2 && ['COM'].includes(row.implementationStatus))
      if (!disabledForSecondCycle && row.createdInCycle != 1 && row.ownedByUser){ */
      const disabledForCycleLowerThanThree = row.createdInCycle < 3 && ['COM'].includes(row.implementationStatus)
      const enabled = !disabledForCycleLowerThanThree && row.ownedByUser
      if (enabled){
        dispatch(measureSet(row))
      }   
    } 

    const onZoomToMeasureItem = (row: IGetMeasure) => {
      const point = row.point;
      if (point && point.coordinates && point.coordinates.length > 1){
        const x = point.coordinates[0];
        const y = point.coordinates[1];
        const formattedBounds = [
          x - 500,
          x + 500,
          y - 500,
          y + 500,
          0,
          0,
        ] as IViewerBounds;
        const { resetCameraToBounds } = MikeVisualizerLib
         resetCameraToBounds(formattedBounds);
      }   
    } 

  const allowedAreaTableNames = useMemo(() => {
    if (isSuccess && areasFromApi != null && areasFromApi.length > 0){  
      function onlyUnique(value, index, array) {
        return array.indexOf(value) === index;
      } 
      const allowedAreas = areasFromApi.filter((area: IWorkingAreaResponse) => area.isPolygonEditable);
      const areaNames = allowedAreas.map((area: IWorkingAreaResponse) => area.sourceTableName);
      const allowedAreaNames = areaNames.filter(onlyUnique); 
      const sortedAreaNames = allowedAreaNames.sort();

      const otherAreas = areasFromApi.filter((area: IWorkingAreaResponse) => !area.isPolygonEditable);
      const otherAreaNames = otherAreas.map((area: IWorkingAreaResponse) => area.sourceTableName);
      const notAllowedAreaNames = otherAreaNames.filter(onlyUnique); 
      const sortedOtherAreaNames = notAllowedAreaNames.sort();

      drawAreaLayer(allowedAreas, sortedAreaNames, otherAreas, sortedOtherAreaNames);
      return sortedAreaNames;
    }
    return [];
  }, [areasFromApi, isSuccess])

 const [pressedPoint, setPressedPoint] = useState<IPressEvent | null>(null);
 const {pixelX, pixelY, x,y, radius} = useMemo(() => {
    const getRadius = (zoomLevel: number) => {
      if (zoomLevel > 14){
        return 1000;
      }
      else if (zoomLevel > 9){
        return 2000;
      }
       else if (zoomLevel > 4){
        return 3000;
      }
      else {
        return 5000;
      }
    }
    if (!drawing && !open && pressedPoint && pressedPoint.pixel && pressedPoint.pixel.length > 1 && pressedPoint.coordinates && pressedPoint.coordinates.length > 1){
      const zoom = dataMap?.getView().getZoom();
      const r = zoom ? getRadius(zoom) : 400;
      const p = pressedPoint.pixel;
      const c = pressedPoint.coordinates;
      return {pixelX: p[0], pixelY: p[1], x: c[0], y: c[1], radius: r}
    }
    return {pixelX:null, pixelY: null, x: null, y: null, r: 400}
  },[pressedPoint, drawing, open])

  useEffect(() => {
     const onViewerClick = (calldata: IPressEvent) => {   
      setPressedPoint(calldata);
    }
    /* const onViewerBoundsChanged = () => {   
      setPressedPoint(null);
    } */
    const events = [onPressExtended(onViewerClick)/* , onBoundsChanged(onViewerBoundsChanged) */]
    return () => {
        events.forEach((unsubscribe) => unsubscribe());       
      };
  },[])

  useEffect(() => {
    if (allowedAreaTableNames.length > 0){
      enableClickPointPicker();
    }
  },[allowedAreaTableNames])


  const { url, attributions } = getBaseMapConfiguration(EBaseMapIds.OSM);    
  const initialBounds = [342771.96558235836,674202.3108069168,5682911.261552873,5971561.060511859,0,0] as IViewerBounds;  

  const setupMap = async () => { 
    const { setCrs, setViewModeTo2D } = MikeVisualizerLib;    
    setCrs("+proj=utm +zone=32 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs", 25832);    
    await setViewModeTo2D(url, attributions!);
  }; 

  const [topOffset, setTopOffset] = useState(0)
  const measuredRef = useCallback(node => {
    if (node !== null) {
      setTopOffset(node.getBoundingClientRect().height)
    }
  }, [])

  const [paginatedAndSortedRows, setPaginatedAndSortedRows] = useState<{
    sortBy: string | ((item: any) => string | number),
    sortDir: 'asc' | 'desc';
    page: number;
    noOfPageRows: number;
    content: Array<any>;
    totalCount: number;
  }>({ sortBy: 'updatedAt', sortDir: 'desc', page: 0, noOfPageRows: 10, content: Array<any>(), totalCount: 0});

  const [showAllMeasures, setShowAllMeasures] = useState(false)

  const drawMeasures = async(measures: Array<IGetMeasure>) => {
     const { update2DData} = MikeVisualizerLib; 
     const otherMeasures = measures.filter((m: IGetMeasure) => !m.ownedByUser)
      if (otherMeasures){
        const fc = createPointFeatureCollection(otherMeasures)
        if (fc){
          await update2DData(fc, OTHER_EXISTING_MEASURES_ID, undefined, undefined, 1, makeDefaultVectorStyle(MIKE_COLORS.ACTIONBLUE_DARK));
          dispatch(applyMeasureVisibility(OTHER_EXISTING_MEASURES_ID));
        }        
     }
     const ownMeasures = measures.filter((m: IGetMeasure) => m.ownedByUser)
     if (ownMeasures){
        const fc = createPointFeatureCollection(ownMeasures)
        if (fc){
          await update2DData(fc, EXISTING_MEASURES_ID, undefined, undefined, 1, makeDefaultVectorStyle(MAP_COLORS.BURGUNDYPLUM))
          dispatch(applyMeasureVisibility(EXISTING_MEASURES_ID));
        }        
     }
     dispatch(legendGroupNameHideAll(layerGroupTitleAllAreas));      
  }

  const measures = useMemo(() => {
    return measuresFromApi?.map(((m: IGetMeasure) => {
      const point = createPoint(m);     
      const actionField = getActionFieldFromMeasureType(m.measureType);
      const euAspect = getEUAspectfromActionField(actionField);
      return {...m, actionField, euAspect, point}
    }))
  },[measuresFromApi])  

  const { measureCreators, measureUpdaters, tableColumns, showEditorFilter } = useMemo(() => {
    function onlyUnique(value, index, array) {
        return array.indexOf(value) === index;
      } 
      if (measuresFromApi){
        const creators = measuresFromApi.map(((m: IGetMeasure) =>  m.createdBy));
        if (creators && creators.length > 0){
          const names = creators.filter(onlyUnique); 
          const emptyNames = names.filter(n => n !== null && n.length > 0); 
          if (emptyNames.length > 0){
            const sorted = emptyNames.sort();
            const updaters = measuresFromApi.map(((m: IGetMeasure) =>  m.updatedBy));
            const updaternames = updaters.filter(onlyUnique); 
            const clearemptyNames = updaternames.filter(n => n !== null && n.length > 0);
            const sortedUpdaters =  clearemptyNames.sort();
            return {showEditorFilter: true, measureCreators: sorted.map((n: string) => {return {id: n, label: n}}), measureUpdaters: sortedUpdaters.map((n: string) => {return {id: n, label: n}}),  tableColumns: columns}; 
          }             
        }
      }
    return {showEditorFilter: false, measureCreators: [], measureUpdaters: [], tableColumns: columns.filter((c: IColumn) => ![UPDATEDBY, CREATEDBY].includes(c.field))};    
  },[measuresFromApi, columns])  

  const projectDevelopers = useMemo(() => {
     function onlyUnique(value, index, array) {
        return array.indexOf(value) === index;
      } 
      if (projectDevelopersFromApi &&  projectDevelopersFromApi.projectDevelopers && projectDevelopersFromApi.projectDevelopers.length > 0){        
        const names = projectDevelopersFromApi.projectDevelopers.filter(onlyUnique); 
        const sorted = names.sort();
        return sorted.map((n: string) => {return {id: n, label: n}});        
      } 
      return [];    
  }, [projectDevelopersFromApi])

  useEffect(() => {  
    if (isSuccessRiver && riversFromApi !== undefined && riversFromApi.length > 0){
      drawRivers(riversFromApi);
    }   

  },[riversFromApi, isSuccessRiver])

  useEffect(() => {  
    if (areaLayersLoaded && measures !== undefined){
      drawMeasures(measures);
    }   

  },[measures, areaLayersLoaded])

  useEffect(() => {    
    const paginatedRows = getSortedPageRows(measures !== undefined ? measures : Array<IGetMeasure>(), paginatedAndSortedRows.sortBy,paginatedAndSortedRows.sortDir,0,paginatedAndSortedRows.noOfPageRows, showAllMeasures, cycleOption, measureIdFilter, ownerCompanyNameFilter, actionFieldFilter, createdByFilter, updatedByFilter);
    setPaginatedAndSortedRows((previous) => {return {...previous, content: paginatedRows.sortedRows as Array<IGetMeasure>, totalCount: paginatedRows.totalCount, page: 0}}) 

  },[showAllMeasures, measures, cycleOption, measureIdFilter, ownerCompanyNameFilter, actionFieldFilter, createdByFilter, updatedByFilter])

  const onSort = useCallback((
    _orderBy: string | ((item: any) => string | number),
    _order: 'asc' | 'desc'
  ) => {
    const paginatedRows = getSortedPageRows(measures !== undefined ? measures : Array<IGetMeasure>(),_orderBy,_order,0,paginatedAndSortedRows.noOfPageRows, showAllMeasures, cycleOption, measureIdFilter, ownerCompanyNameFilter, actionFieldFilter, createdByFilter, updatedByFilter);
    setPaginatedAndSortedRows((previous) => {return {...previous, content: paginatedRows.sortedRows,sortBy: _orderBy, sortDir: _order, page: 0, totalCount: paginatedRows.totalCount}}) 
  }, [paginatedAndSortedRows.noOfPageRows, showAllMeasures, measures, cycleOption, measureIdFilter, ownerCompanyNameFilter, actionFieldFilter, createdByFilter, updatedByFilter])

  const onChangePage = useCallback((
    newPage: number) => {
    const paginatedRows = getSortedPageRows(measures !== undefined ? measures : Array<IGetMeasure>(),paginatedAndSortedRows.sortBy,paginatedAndSortedRows.sortDir,newPage,paginatedAndSortedRows.noOfPageRows, showAllMeasures, cycleOption, measureIdFilter, ownerCompanyNameFilter, actionFieldFilter, createdByFilter, updatedByFilter);
    setPaginatedAndSortedRows((previous) => {return {...previous, content: paginatedRows.sortedRows, page: newPage, totalCount: paginatedRows.totalCount}}) 
  }, [paginatedAndSortedRows, showAllMeasures, measures, cycleOption, measureIdFilter, ownerCompanyNameFilter, actionFieldFilter, createdByFilter, updatedByFilter])

  const onChangeRowsPerPage = useCallback((
    newNoOfPageRows: number) => {
    const paginatedRows = getSortedPageRows(measures !== undefined ? measures : Array<IGetMeasure>(),paginatedAndSortedRows.sortBy,paginatedAndSortedRows.sortDir,paginatedAndSortedRows.page, newNoOfPageRows, showAllMeasures, cycleOption, measureIdFilter, ownerCompanyNameFilter, actionFieldFilter, createdByFilter, updatedByFilter);
    setPaginatedAndSortedRows((previous) => {return {...previous, content: paginatedRows.sortedRows, noOfPageRows: newNoOfPageRows, totalCount: paginatedRows.totalCount}}) 
  }, [paginatedAndSortedRows, showAllMeasures, measures, cycleOption, measureIdFilter, ownerCompanyNameFilter, actionFieldFilter, createdByFilter, updatedByFilter])

  const openMeasureDialog = () => {
    dispatch(measureDialogOpened(true))
  } 

   const handleMeasureTableButtonClicked = (showAll: boolean) => {
    setShowAllMeasures(showAll)
  }

  const renderEditItem = (row: IGetMeasure) => {   
      if (row){
    /*     const disabledForSecondCycle = row.updatedInCycle === 1 || (row.createdInCycle === 2 && ['COM'].includes(row.implementationStatus))
        const enabled = !disabledForSecondCycle && row.ownedByUser && row.createdInCycle > 1 */
        const disabledForCycleLowerThanThree = row.createdInCycle < 3 && ['COM'].includes(row.implementationStatus)
        const enabled = !disabledForCycleLowerThanThree && row.ownedByUser
         return (
          <div css={actionStyle(!enabled)}>
            <Edit css={iconStyle(!enabled)} />
          </div>
        )
      }     
    }

     const renderZoomToMeasureItem = (row: IGetMeasure) => {   
      if (row){       
         return (
          <div>
            <ZoomToArea />
          </div>
        )
      }     
    }

  const handleCycleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setCycleOption((event.target as HTMLInputElement).value);
  };

  const handleMeasureIdFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setMeasureIdFilter((event.target as HTMLInputElement).value);
  };

  const handleResetMeasureIdFilter = () => {
    setMeasureIdFilter("")
  }

  const handleOwnerCompanyNameFilterChange = (_event, value: any) => {
      setOwnerCompanyNameFilter(value ? value : undefined);
  };

  const handleActionFieldFilterChange = (_event, value: any) => {
    setActionFieldFilter(value ? value : undefined);
  };

  const handleCreatedByFilterChange = (_event, value: any) => {
    setCreatedByFilter(value ? value : undefined);
  };

  const handleUpdatedByFilterChange = (_event, value: any) => {
    setUpdatedByFilter(value ? value : undefined);
  };


  return (  
    <>
      <MikeVisualizerContainer sidePanelOpen={open}>       
        {measuresFromApi && pixelX && pixelY  && x && y && <MeasureInfo radius={radius} pixelX={pixelX} pixelY={pixelY} x={x} y={y} measuresFromApi={measuresFromApi}/>}
        <MikeVisualizer bounds={initialBounds} onReady={setupMap}/>
        <ViewerTools/>
        <ViewerLegend/>
        {!open && <MeasureButtonPanel disabled={allowedAreaTableNames.length === 0} onOpenMeasureDialog={openMeasureDialog}/>}
        <MikeVisualizerStoryDetails ref={measuredRef}> 
          <div >
            <div css={tableModesStyle} >
              <MikeButton css={buttonStyle} onClick={() => handleMeasureTableButtonClicked(true)} variant={showAllMeasures ? "outlined" : "text"}>Alle Maßnahmen</MikeButton>
              <MikeButton css={buttonStyle} onClick={() => handleMeasureTableButtonClicked(false)} variant={!showAllMeasures ? "outlined" : "text"}>Zugeordnete Maßnahmen</MikeButton>
            <div css={cycleOptionStyle} >
              <Typography css={cycleOptionFilterLabel}>{"Zyklus"}</Typography>
              <FormControl>
                <RadioGroup
                  row
                  aria-labelledby="cycleOptions-row-radio-buttons-group-label"
                  name="row-radio-buttons-group"
                  value={cycleOption}
                  onChange={handleCycleOptionChange}
                >
                  <FormControlLabel value="all" control={<Radio />} label="Alle" />
                  <FormControlLabel value="1" control={<Radio />} label="1" />
                  <FormControlLabel value="2" control={<Radio />} label="2" />
                  <FormControlLabel value="3" control={<Radio />} label="3" />               
                </RadioGroup>
              </FormControl>
            </div>
            <div css={measureIdFilterContainer}>
              <FilterInput value={measureIdFilter} onChangeValue={handleMeasureIdFilterChange} onResetValue={handleResetMeasureIdFilter} placeHolder={'Maßnahmen-ID'}/>
            </div>
          <div css={filterContainerStyle(showEditorFilter ? "15%" : "30%")}>  
              <Autocomplete  
                 style={{ minWidth: '8em', whiteSpace: 'nowrap' }}
                 options={projectDevelopers}                 
                 fullWidth={true}      
                 onChange={(e,val) => handleOwnerCompanyNameFilterChange(e,val)}               
                 size="small"
                 value={ownerCompanyNameFilter}     
                 renderInput={(params) => (
                   <TextField
                     {...params}
                     label="Vorhabenträger"
                     slotProps={{
                       htmlInput: {
                         ...params.inputProps,
                         autoComplete: 'new-password', // disable autocomplete and autofill
                       },
                     }}
                   />
                 )}
               />   
            {/* <ContextHelp helpTexts={["Eine Liste der Vorhabenträger befindet sich am Ende der Bedienungsanleitung."]} primary/> */}
            </div>

            <div css={filterContainerStyle(showEditorFilter ? "15%" : "30%")}>  
              <Autocomplete  
                 style={{ minWidth: '8em', whiteSpace: 'nowrap' }}
                 options={actionFields}                 
                 fullWidth={true}      
                 onChange={(e,val) => handleActionFieldFilterChange(e,val)}               
                 size="small"
                 value={actionFieldFilter}     
                 renderInput={(params) => (
                   <TextField
                     {...params}
                     label="Handlungsfeld"
                     slotProps={{
                       htmlInput: {
                         ...params.inputProps,
                         autoComplete: 'new-password', // disable autocomplete and autofill
                       },
                     }}
                   />
                 )}
               /> 
            </div>


            {showEditorFilter ? <div css={filterContainerStyle(showEditorFilter ? "15%" : "30%")}>  
              <Autocomplete  
                 style={{ minWidth: '8em', whiteSpace: 'nowrap' }}
                 options={measureCreators}                 
                 fullWidth={true}      
                 onChange={(e,val) => handleCreatedByFilterChange(e,val)}               
                 size="small"
                 value={createdByFilter}     
                 renderInput={(params) => (
                   <TextField
                     {...params}
                     label="Gemeldet von"
                     slotProps={{
                       htmlInput: {
                         ...params.inputProps,
                         autoComplete: 'new-password', // disable autocomplete and autofill
                       },
                     }}
                   />
                 )}
               /> 
            </div> : null}.

            {measureUpdaters.length > 0 ? <div css={filterContainerStyle(showEditorFilter ? "15%" : "30%")}>  
              <Autocomplete  
                 style={{ minWidth: '8em', whiteSpace: 'nowrap' }}
                 options={measureUpdaters}                 
                 fullWidth={true}      
                 onChange={(e,val) => handleUpdatedByFilterChange(e,val)}               
                 size="small"
                 value={updatedByFilter}     
                 renderInput={(params) => (
                   <TextField
                     {...params}
                     label="Letzter Bearbeiter"
                     slotProps={{
                       htmlInput: {
                         ...params.inputProps,
                         autoComplete: 'new-password', // disable autocomplete and autofill
                       },
                     }}
                   />
                 )}
               /> 
            </div> : null}
            
            </div>
            <DataTable
              topOffset={topOffset} 
              selectable={false}
              onCellClick={() => {}}
              onSelectionChange={() => {}}
              selectedRows={[]}
              actions={[
                {
                    index: 1,
                    name: 'Edit',
                    callBack: onEdit,
                    render: renderEditItem,
                    disableGutters: true
                },
                {
                    index: 2,
                    name: 'Zoom',
                    callBack: onZoomToMeasureItem,
                    render: renderZoomToMeasureItem,
                    disableGutters: true
                }
              ]} 
              loading={isLoadingMeasures || isFetchingMeasures}
              columns={tableColumns}
              idField="id"
              data={paginatedAndSortedRows.content}     
              onHandleRequestSort={onSort}
              pagination={true}
              page={paginatedAndSortedRows.page}                  
              onChangePage={onChangePage}
              onChangeRowsPerPage={onChangeRowsPerPage}
              totalCount={paginatedAndSortedRows.totalCount}
              order={paginatedAndSortedRows.sortDir}
              orderBy={paginatedAndSortedRows.sortBy}
              rowsPerPage={paginatedAndSortedRows.noOfPageRows}
          />    
          </div>          
        </MikeVisualizerStoryDetails>
      </MikeVisualizerContainer>
      {open && <MeasureDialog projectDevelopers={projectDevelopers} areaNames={allowedAreaTableNames} />}
    </>    
  ) 
}

export default Viewer  
